<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useWorkspaceContext } from '@/composables/use-workspace-context';
import { usePanelHandle } from '@/composables/use-panels';
import { Progress } from '@/components/ui/progress';

const props = defineProps<{
	path: string;
}>();

const ctx = useWorkspaceContext();

const fileName = props.path.split('/').pop() ?? props.path;
const handle = usePanelHandle(fileName);

const objectUrl = ref<string | null>(null);
const mediaType = ref<'image' | 'video' | 'audio'>('image');

const MIME_MAP: Record<string, string> = {
	'.png': 'image/png',
	'.jpg': 'image/jpeg',
	'.jpeg': 'image/jpeg',
	'.gif': 'image/gif',
	'.webp': 'image/webp',
	'.svg': 'image/svg+xml',
	'.bmp': 'image/bmp',
	'.ico': 'image/x-icon',
	'.avif': 'image/avif',
	'.mp4': 'video/mp4',
	'.webm': 'video/webm',
	'.ogg': 'video/ogg',
	'.mov': 'video/mp4',
	'.mp3': 'audio/mpeg',
	'.wav': 'audio/wav',
	'.flac': 'audio/flac',
	'.aac': 'audio/aac',
	'.oga': 'audio/ogg',
	'.weba': 'audio/webm',
	'.m4a': 'audio/mp4',
};

const VIDEO_EXTENSIONS = new Set(['.mp4', '.webm', '.ogg', '.mov']);
const AUDIO_EXTENSIONS = new Set(['.mp3', '.wav', '.flac', '.aac', '.oga', '.weba', '.m4a']);

const ext = ('.' + (fileName.split('.').pop() ?? '')).toLowerCase();
mediaType.value = VIDEO_EXTENSIONS.has(ext)
	? 'video'
	: AUDIO_EXTENSIONS.has(ext)
		? 'audio'
		: 'image';
const mimeType = MIME_MAP[ext] ?? 'application/octet-stream';

onMounted(async () => {
	handle.loadProgress = 0;

	const bytes = await ctx.readFile(props.path, (percent) => {
		handle.loadProgress = percent;
	});

	if (!bytes) {
		handle.loadProgress = null;
		handle.error = 'Failed to load file — SFTP session unavailable or file not accessible.';
		return;
	}

	const blob = new Blob([bytes], { type: mimeType });
	objectUrl.value = URL.createObjectURL(blob);
	handle.loadProgress = null;
});

onUnmounted(() => {
	if (objectUrl.value) {
		URL.revokeObjectURL(objectUrl.value);
		objectUrl.value = null;
	}
});
</script>

<template>
	<div class="relative flex size-full items-center justify-center bg-editor p-10">
		<!-- loading -->
		<div
			v-if="handle.loadProgress != null"
			class="size-full flex flex-col items-center justify-center gap-2 px-16"
		>
			<Progress :model-value="handle.loadProgress" class="max-w-64" />
			<span class="text-xs text-muted-foreground">{{ handle.loadProgress }}%</span>
		</div>

		<!-- error -->
		<div v-else-if="handle.error" class="flex items-center justify-center text-sm text-destructive">
			{{ handle.error }}
		</div>

		<!-- image -->
		<img
			v-else-if="objectUrl && mediaType === 'image'"
			:src="objectUrl"
			:alt="fileName"
			class="max-h-full max-w-full object-contain shadow-md rounded"
		/>

		<!-- video -->
		<video
			v-else-if="objectUrl && mediaType === 'video'"
			:src="objectUrl"
			controls
			class="max-h-full max-w-full shadow-md rounded"
		>
			Your browser does not support this video format.
		</video>

		<!-- audio -->
		<audio
			v-else-if="objectUrl && mediaType === 'audio'"
			:src="objectUrl"
			controls
			class="w-full max-w-md"
		>
			Your browser does not support this audio format.
		</audio>
	</div>
</template>
